import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  Card,
  CardContent,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Fade,
  useTheme,
  alpha,
  Tooltip,
  styled
} from '@mui/material';
import {
  ArrowUpward as SpendIcon,
  ArrowDownward as EarnIcon,
  SwapHoriz as TransferIcon,
  CurrencyExchange as ExchangeIcon,
  Receipt as ReceiptIcon,
  FilterAlt as FilterIcon,
  History as HistoryIcon,
  MoreHoriz as MoreIcon,
  ContentCopy as CopyIcon,
  BarChart as ChartIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { tokenWalletService } from '../../../services/skillcoinService';
import { motion } from 'framer-motion';

// Custom styled components
const TransactionItem = styled(motion.div)(({ theme, type }) => {
  const colors = {
    earn: theme.palette.success.main,
    spend: theme.palette.error.main,
    transfer_in: theme.palette.info.main,
    transfer_out: theme.palette.warning.main,
    exchange_in: theme.palette.secondary.main,
    exchange_out: theme.palette.secondary.dark
  };
  
  const baseColor = colors[type] || theme.palette.grey[500];
  
  return {
    position: 'relative',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(145deg, ${alpha(baseColor, 0.05)} 0%, ${alpha(baseColor, 0.02)} 100%)`,
    boxShadow: `0 4px 12px ${alpha(baseColor, 0.1)}`,
    border: `1px solid ${alpha(baseColor, 0.2)}`,
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 16px ${alpha(baseColor, 0.2)}`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: baseColor,
      borderTopLeftRadius: theme.shape.borderRadius * 2,
      borderBottomLeftRadius: theme.shape.borderRadius * 2,
    }
  };
});

const HeaderCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, ${alpha('#fff', 0.1)} 0%, ${alpha('#fff', 0)} 50%, ${alpha('#fff', 0.1)} 100%)`,
    zIndex: 1,
    opacity: 0.7,
  }
}));

const CircleDecoration = styled('div')(({ theme, size = 100, top, right, left, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.common.white, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  zIndex: 0,
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
  borderRadius: 20,
  margin: theme.spacing(0.5),
  fontSize: '0.75rem',
  textTransform: 'none',
  boxShadow: active ? `0 4px 10px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
  backgroundColor: active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.1),
  color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.2),
  }
}));

const AmountText = styled(Typography)(({ theme, type }) => {
  const colors = {
    earn: theme.palette.success.main,
    spend: theme.palette.error.main,
    transfer_in: theme.palette.info.main,
    transfer_out: theme.palette.warning.main,
    exchange_in: theme.palette.secondary.main,
    exchange_out: theme.palette.secondary.dark
  };
  
  return {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: colors[type] || theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    fontFamily: "'Roboto Mono', monospace",
  };
});

const TokenTransactionHistory = ({ 
  tokenId, 
  limit = 5,
  transactions: providedTransactions = null,
  transactionStats: providedStats = null
}) => {
  const [transactions, setTransactions] = useState(providedTransactions || []);
  const [loading, setLoading] = useState(!providedTransactions);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10
  });
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (providedTransactions) {
      setTransactions(providedTransactions);
      setLoading(false);
      return;
    }
    
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        // Add cache busting to prevent stale data
        const timestamp = new Date().getTime();
        const response = await tokenWalletService.getTransactions(tokenId, { 
          limit: 100,
          cacheBust: timestamp
        });
        
        if (response.success) {
          setTransactions(removeDuplicateTransactions(response.data || []));
        } else {
          setError(response.error || 'Failed to fetch transactions');
        }
      } catch (err) {
        setError('Failed to fetch transactions');
        console.error('Error fetching transactions:', err);
      } finally {
        setLoading(false);
      }
    };

    if (tokenId) {
      fetchTransactions();
    }
  }, [tokenId, providedTransactions]);

  const removeDuplicateTransactions = (transactions) => {
    if (!Array.isArray(transactions)) return [];
    
    const uniqueTransactions = [];
    const duplicateTracker = new Map();
    
    // Sort transactions by date descending
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
    );
    
    // First pass: Group transactions by their core properties
    sortedTransactions.forEach(transaction => {
      // For "Reward for completing" transactions, extract the actual activity name
      let baseDescription = '';
      const originalDesc = (transaction.description || '').trim();
      
      if (originalDesc.toLowerCase().includes('reward for completing')) {
        baseDescription = originalDesc.replace(/reward for completing:?\s*/i, '').trim();
      } else {
        baseDescription = originalDesc;
      }
      
      // Clean the description for better matching
      baseDescription = baseDescription.toLowerCase();
      
      // Extract date components for time-window matching
      const dateObj = new Date(transaction.createdAt || transaction.date);
      const dateString = `${dateObj.getFullYear()}-${dateObj.getMonth()+1}-${dateObj.getDate()}`;
      const hourMinute = `${dateObj.getHours()}:${dateObj.getMinutes()}`;
      
      // Completely remove earned transactions with source wallet
      if (transaction.type === 'earn') {
        const desc = originalDesc.toLowerCase();
        
        // Skip earned transactions with wallet as source
        if ((transaction.metadata && transaction.metadata.source && 
             (transaction.metadata.source.toLowerCase() === 'wallet' || 
              transaction.metadata.source.toLowerCase().includes('wallet'))) ||
            (transaction.source && 
             (transaction.source.toLowerCase() === 'wallet' || 
              transaction.source.toLowerCase().includes('wallet'))) ||
            (desc.includes('wallet')) ||
            (transaction.sourceType && transaction.sourceType.toLowerCase().includes('wallet')) ||
            (transaction.metadata && transaction.metadata.sourceType && 
             transaction.metadata.sourceType.toLowerCase().includes('wallet')) ||
            (transaction.fromWallet) ||
            (transaction.metadata && transaction.metadata.fromWallet)) {
          return;
        }
        
        // Skip if manual credit, completely removing this functionality
        if (desc.includes('manual credit') || 
            desc.includes('manualcredit') ||
            desc.includes('credit adjustment') ||
            (transaction.description && transaction.description.toLowerCase().includes('manual')) ||
            (transaction.metadata && transaction.metadata.isManual)) {
          return;
        }
      }
      
      // Create a unique group key based on the cleaned description, type, and date
      const groupKey = `${baseDescription}_${transaction.type}_${dateString}_${transaction.amount}`;
      
      // Add transaction to group or create new group
      if (!duplicateTracker.has(groupKey)) {
        duplicateTracker.set(groupKey, []);
      }
      
      duplicateTracker.get(groupKey).push({
        transaction,
        dateTime: dateObj,
        hourMinute,
        originalDesc
      });
    });
    
    // Second pass: Process each group to select the transaction to keep
    duplicateTracker.forEach((group, key) => {
      if (group.length === 1) {
        // If only one transaction in the group, keep it
        uniqueTransactions.push(group[0].transaction);
        return;
      }
      
      // If multiple transactions in the same group, need to decide which to keep
      let bestTransaction = group[0]; // Default to the first one
      
      // Check for time proximity (transactions within 10 minutes of each other)
      const isTimeProximity = group.some((item, i) => {
        if (i === 0) return false;
        
        const timeDiff = Math.abs(item.dateTime - group[0].dateTime);
        return timeDiff < 10 * 60 * 1000; // 10 minutes in milliseconds
      });
      
      if (isTimeProximity) {
        // If transactions are close in time, they're likely duplicates
        
        // Prefer transactions without "Reward for completing" prefix for cleaner display
        const withoutPrefix = group.find(item => 
          !item.originalDesc.toLowerCase().includes('reward for completing')
        );
        
        if (withoutPrefix) {
          bestTransaction = withoutPrefix;
        } else {
          // Otherwise, prefer the one with the most descriptive name
          bestTransaction = group.reduce((best, current) => 
            current.originalDesc.length > best.originalDesc.length ? current : best
          );
        }
        
        console.log(
          `Detected duplicate transactions "${key}". Keeping: "${bestTransaction.originalDesc}" and filtering out ${group.length - 1} duplicates.`
        );
      } else {
        // If the transactions are not close in time, keep them all
        group.forEach(item => {
          if (item !== bestTransaction) {
            uniqueTransactions.push(item.transaction);
          }
        });
      }
      
      // Add the best transaction from each group
      uniqueTransactions.push(bestTransaction.transaction);
    });
    
    return uniqueTransactions;
  };

  const displayedTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) {
      console.warn('Transactions is not an array:', transactions);
      return [];
    }
    
    // Filter transactions based on selected type
    let filtered = transactions;
    if (filter !== 'all') {
      filtered = transactions.filter(tx => tx.type === filter);
    }
    
    const displayLimit = expanded ? filtered.length : limit;
    return filtered.slice(0, displayLimit);
  }, [transactions, limit, expanded, filter]);
  
  const stats = useMemo(() => {
    if (providedStats) {
      return {
        earned: providedStats.earned || providedStats.totalEarned || 0,
        spent: providedStats.spent || providedStats.totalSpent || 0,
        sent: providedStats.sent || providedStats.totalSent || 0
      };
    }
    
    if (!Array.isArray(transactions)) return { earned: 0, spent: 0, sent: 0 };
    
    return transactions.reduce((acc, tx) => {
      const amount = parseFloat(tx.amount) || 0;
      
      // Enhanced transaction type detection
      if (tx.type === 'earn' || tx.type === 'credit' || tx.type === 'transfer_in' || 
          tx.transactionType === 'credit') {
        acc.earned += amount;
      } else if (tx.type === 'spend' || 
                (tx.type === 'debit' && !tx.description?.toLowerCase().includes('transfer')) ||
                (tx.transactionType === 'debit' && !tx.description?.toLowerCase().includes('transfer'))) {
        acc.spent += amount;
      } else if (tx.type === 'transfer_out' || 
                (tx.type === 'debit' && tx.description?.toLowerCase().includes('transfer')) ||
                (tx.transactionType === 'debit' && tx.description?.toLowerCase().includes('transfer'))) {
        acc.sent += amount;
      }
      
      return acc;
    }, { earned: 0, spent: 0, sent: 0 });
  }, [transactions, providedStats]);

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'earn':
        return <EarnIcon sx={{ color: 'success.main' }} />;
      case 'spend':
        return <SpendIcon sx={{ color: 'error.main' }} />;
      case 'transfer_in':
        return <TransferIcon sx={{ color: 'info.main' }} />;
      case 'transfer_out':
        return <TransferIcon sx={{ color: 'warning.main' }} />;
      case 'exchange_in':
      case 'exchange_out':
        return <ExchangeIcon sx={{ color: 'secondary.main' }} />;
      default:
        return <ReceiptIcon />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'earn':
        return 'Earned';
      case 'spend':
        return 'Spent';
      case 'transfer_in':
        return 'Received';
      case 'transfer_out':
        return 'Sent';
      case 'exchange_in':
        return 'Exchanged In';
      case 'exchange_out':
        return 'Exchanged Out';
      default:
        return type;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'earn':
      case 'transfer_in':
        return 'success';
      case 'spend':
      case 'transfer_out':
        return 'error';
      case 'exchange_in':
        return 'secondary';
      case 'exchange_out':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAmountDisplay = (transaction) => {
    const prefix =
      transaction.type === 'earn' || transaction.type === 'transfer_in' || transaction.type === 'exchange_in'
        ? '+'
        : transaction.type === 'spend' || transaction.type === 'transfer_out' || transaction.type === 'exchange_out'
        ? '-'
        : '';

    return `${prefix}${transaction.amount}`;
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        flexDirection="column" 
        p={3} 
        sx={{ 
          height: '300px',
          background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.6)}, ${alpha(theme.palette.background.paper, 0.8)})`,
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
        <Typography variant="body1" color="textSecondary">
          Loading your transaction history...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          mt: 2,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
        variant="filled"
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <HeaderCard elevation={4} sx={{ mb: 3 }}>
        <CircleDecoration size={120} top="-20px" right="-20px" opacity={0.15} />
        <CircleDecoration size={80} bottom="-10px" left="30%" opacity={0.1} />
        
        <CardContent sx={{ position: 'relative', zIndex: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2" sx={{ 
              fontWeight: 700, 
              display: 'flex', 
              alignItems: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <HistoryIcon sx={{ mr: 1 }} /> Transaction History
            </Typography>
            <Box display="flex" alignItems="center">
              <Chip 
                label={`${transactions.length} Transactions`} 
                size="small" 
                sx={{ 
                  background: alpha(theme.palette.common.white, 0.2),
                  color: theme.palette.common.white,
                  fontWeight: 500,
                  backdropFilter: 'blur(4px)',
                  mr: 1
                }} 
              />
              <IconButton
                size="small"
                onClick={() => {
                  if (tokenId) {
                    setLoading(true);
                    // Add extra randomness to prevent caching
                    tokenWalletService.getTransactions(tokenId, { 
                      limit: 500,
                      cacheBust: Date.now() + Math.random()
                    })
                      .then(response => {
                        if (response.success) {
                          setTransactions(removeDuplicateTransactions(response.data || []));
                        } else {
                          setError(response.error || 'Failed to refresh transactions');
                        }
                      })
                      .catch(err => {
                        setError('Failed to refresh transactions');
                        console.error('Error refreshing transactions:', err);
                      })
                      .finally(() => {
                        setLoading(false);
                      });
                  }
                }}
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  color: theme.palette.common.white,
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.2) }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total Earned
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  +{stats.earned.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total Spent
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  -{stats.spent.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total Sent
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  -{stats.sent.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, pt: 1, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.2)}` }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Available Balance:
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {(stats.earned - stats.spent - stats.sent).toFixed(2)}
            </Typography>
          </Box>
        </CardContent>
      </HeaderCard>

      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          mb: 2, 
          justifyContent: 'center', 
          background: alpha(theme.palette.background.paper, 0.7),
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          py: 1,
          px: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        <FilterButton 
          size="small" 
          onClick={() => setFilter('all')}
          active={filter === 'all'}
        >
          All
        </FilterButton>
        <FilterButton 
          size="small" 
          onClick={() => setFilter('earn')}
          active={filter === 'earn'}
          startIcon={<EarnIcon fontSize="small" />}
        >
          Earned
        </FilterButton>
        <FilterButton 
          size="small" 
          onClick={() => setFilter('spend')}
          active={filter === 'spend'}
          startIcon={<SpendIcon fontSize="small" />}
        >
          Spent
        </FilterButton>
        <FilterButton 
          size="small" 
          onClick={() => setFilter('transfer_in')}
          active={filter === 'transfer_in'}
          startIcon={<TransferIcon fontSize="small" />}
        >
          Received
        </FilterButton>
        <FilterButton 
          size="small" 
          onClick={() => setFilter('transfer_out')}
          active={filter === 'transfer_out'}
          startIcon={<TransferIcon fontSize="small" />}
        >
          Sent
        </FilterButton>
      </Box>

      {displayedTransactions.length === 0 ? (
        <Paper 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            borderRadius: 4, 
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box 
            component="img" 
            src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png" 
            alt="No transactions" 
            sx={{ width: 100, height: 100, opacity: 0.7, mb: 2 }}
          />
          <Typography variant="h6" color="textSecondary" paragraph>
            No transactions found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
            Start earning or using your tokens to see your activity here.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <List sx={{ p: 0 }}>
            {displayedTransactions.map((transaction, index) => (
              <TransactionItem
                key={transaction._id || index}
                type={transaction.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette[getTransactionColor(transaction.type)].main, 0.1), 
                        color: theme.palette[getTransactionColor(transaction.type)].main,
                        width: 48,
                        height: 48,
                        boxShadow: `0 4px 14px ${alpha(theme.palette[getTransactionColor(transaction.type)].main, 0.3)}`
                      }}
                    >
                      {getTransactionIcon(transaction.type)}
                    </Avatar>
                  </Grid>
                  
                  <Grid item xs>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {transaction.description || getTransactionLabel(transaction.type)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Chip
                        label={getTransactionLabel(transaction.type)}
                        size="small"
                        sx={{ 
                          mr: 1, 
                          backgroundColor: alpha(theme.palette[getTransactionColor(transaction.type)].main, 0.1),
                          color: theme.palette[getTransactionColor(transaction.type)].main,
                          fontWeight: 500,
                          borderRadius: '4px'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(transaction.createdAt)}
                      </Typography>
                    </Box>
                    
                    {transaction.note && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {transaction.note}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item>
                    <AmountText type={transaction.type} variant="body1">
                      {getAmountDisplay(transaction)}
                    </AmountText>
                  </Grid>
                </Grid>
              </TransactionItem>
            ))}
          </List>

          {transactions.length > limit && !expanded && (
            <Box textAlign="center" mt={2}>
              <Button
                variant="outlined"
                size="medium"
                onClick={() => setExpanded(true)}
                sx={{ 
                  borderRadius: 28,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)' }
                }}
              >
                View All {transactions.length} Transactions
              </Button>
            </Box>
          )}
          
          {expanded && (
            <Box textAlign="center" mt={2}>
              <Button
                variant="outlined"
                size="medium"
                onClick={() => setExpanded(false)}
                sx={{ 
                  borderRadius: 28,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Show Less
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TokenTransactionHistory;